import React, { useEffect, useRef, useState } from "react";

/**
 * CursorDot — custom cursor with a small solid dot following the mouse precisely
 * and a larger ring lagging behind with smooth easing. Grows / changes color on
 * interactive elements (a, button, [data-cursor=link]).
 *
 * Hidden on touch / small screens.
 */
export default function CursorDot() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const target = useRef({ x: -100, y: -100 });
  const dotPos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });
  const rafRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [onDark, setOnDark] = useState(false);

  useEffect(() => {
    // Disable on touch / coarse pointer
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setEnabled(mq.matches);
    const onChange = (e) => setEnabled(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    document.body.style.cursor = "none";

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;

      // Detect if cursor is over a dark surface (rough check using elementFromPoint)
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el) {
        let node = el;
        let dark = false;
        for (let i = 0; i < 4 && node; i++) {
          const bg = window.getComputedStyle(node).backgroundColor;
          if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
            const rgb = bg.match(/\d+/g);
            if (rgb && rgb.length >= 3) {
              const lum =
                (parseInt(rgb[0], 10) * 0.299 +
                  parseInt(rgb[1], 10) * 0.587 +
                  parseInt(rgb[2], 10) * 0.114) /
                255;
              dark = lum < 0.4;
              break;
            }
          }
          node = node.parentElement;
        }
        setOnDark(dark);
      }

      // Interactive hover state
      const interactive =
        el && el.closest(
          "a, button, input, textarea, select, label, [data-cursor='link']"
        );
      setHovering(Boolean(interactive));
    };

    const onLeave = () => {
      target.current.x = -100;
      target.current.y = -100;
    };

    const tick = () => {
      // Dot — fast follow
      dotPos.current.x += (target.current.x - dotPos.current.x) * 0.45;
      dotPos.current.y += (target.current.y - dotPos.current.y) * 0.45;
      // Ring — slower follow
      ringPos.current.x += (target.current.x - ringPos.current.x) * 0.18;
      ringPos.current.y += (target.current.y - ringPos.current.y) * 0.18;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${dotPos.current.x - 4}px, ${dotPos.current.y - 4}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringPos.current.x - 18}px, ${ringPos.current.y - 18}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  const orange = "#ff3301";
  const ringScale = hovering ? 1.55 : 1;
  const dotScale = hovering ? 0.55 : 1;

  return (
    <>
      {/* Outer ring */}
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 36,
          height: 36,
          borderRadius: 9999,
          border: `1.5px solid ${onDark ? "rgba(255,255,255,0.75)" : orange}`,
          background: hovering
            ? onDark
              ? "rgba(255,255,255,0.10)"
              : "rgba(255,51,1,0.10)"
            : "transparent",
          pointerEvents: "none",
          zIndex: 9999,
          transition:
            "border-color 200ms ease, background 200ms ease, transform 60ms linear, scale 300ms cubic-bezier(.2,.8,.2,1)",
          scale: String(ringScale),
        }}
      />
      {/* Inner dot */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: onDark ? "#ffffff" : orange,
          pointerEvents: "none",
          zIndex: 10000,
          transition:
            "background 200ms ease, transform 30ms linear, scale 300ms cubic-bezier(.2,.8,.2,1)",
          scale: String(dotScale),
          boxShadow: onDark
            ? "0 0 12px rgba(255,255,255,0.4)"
            : "0 0 10px rgba(255,51,1,0.4)",
        }}
      />
    </>
  );
}
